import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import * as dailyInsightsActions from '../../store/customers/dailyInsights/actions';
import * as customersActions from '../../store/customers/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
  isAdmin as isAdminSelector,
  userCustomerId,
} from '../../selectors/auth';
import { selectCustomer } from '../../selectors/customer';

import CustomerMetrics from '../../components/Customers/CustomerMetrics';
import Modal from '../../components/Common/Modal/Modal';
import ShowSingleElement from '../../components/Common/ShowSingleElement';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DateRangeFilter from '../../components/Common/DateRangeFilter/DateRangeFilter';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/Customers/DailyInsights/tableColumns';
import {
  ExportButtonPlacement,
  ToggleColumnButton,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const DailyInsights = (props) => {
  const isAdmin = useSelector(isAdminSelector);
  const customerId = useSelector(userCustomerId);
  const customer = useSelector(selectCustomer(customerId));
  const showRebate = customer ? customer.showRebate : true;
  const isAgency = customer ? customer.isAgency : false;

  const dailyInsights = useSelector(
    (state) => state.DailyCustomerInsights.insights,
  );
  const monetizationOrders = useSelector(
    (state) => state.MonetizationOrder.monetizationOrders,
  );
  const customers = useSelector((state) => state.Customer.customers);
  const loading = useSelector((state) => state.DailyCustomerInsights.loading);

  const [selectedInsight, setSelectedInsight] = useState(null);
  const [viewModal, setViewModal] = useState(false);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const startDate = useSelector(
    (state) => state.DailyCustomerInsights.startDate,
  );
  const endDate = useSelector((state) => state.DailyCustomerInsights.endDate);
  const dataFilters = useSelector(
    (state) => state.DailyCustomerInsights.filters,
  );
  const momentStartDate = startDate ? moment(startDate) : null;
  const momentEndDate = endDate ? moment(endDate) : null;

  const [selectedFilters, setSelectedFilters] = useState({
    startDate: momentStartDate || moment.utc().startOf('month'),
    endDate: momentEndDate || moment.utc(),
    customerId: dataFilters?.customerId || customerId || '',
    monetizationOrderIds: dataFilters?.monetizationOrderIds || null,
  });

  const dispatch = useDispatch();

  const refresh = useCallback(
    ({ startDate, endDate, customerId, monetizationOrderIds }) => {
      const selectedStartDate = startDate.utc().startOf('day').toDate();
      const selectedEndDate = endDate.utc().endOf('day').toDate();
      dispatch(
        dailyInsightsActions.fetchDailyInsights(
          selectedStartDate,
          selectedEndDate,
          {
            monetizationOrderIds,
            customerId,
          },
        ),
      );
      setSelectedFilters({
        startDate,
        endDate,
        monetizationOrderIds,
        customerId,
      });
    },
  );

  useEffect(() => {
    dailyInsights.length === 0 && refresh(selectedFilters);
    dispatch(customersActions.syncCustomers());
  }, []);

  const openViewModal = (insight) => {
    setSelectedInsight(insight);
    setViewModal(true);
  };

  const { t } = useTranslation();

  const columns = useMemo(
    () =>
      createTableColumns(
        monetizationOrders,
        customers,
        isAdmin,
        t,
        openViewModal,
        isAgency,
      ),
    [monetizationOrders, customers, isAdmin, t, isAgency],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Daily Customer Insights"
            breadcrumbItem="List"
            onRefresh={() => refresh(selectedFilters)}
            loading={loading}
          />
          <DateRangeFilter
            onUpdate={refresh}
            startDate={selectedFilters.startDate}
            endDate={selectedFilters.endDate}
            loading={loading}
            showCustomerFilters
            initialFilters={selectedFilters}
          />
          <Row>
            <CustomerMetrics showRebate={showRebate} isAgency={isAgency} />
          </Row>
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end mb-2">
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={dailyInsights}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Modal
            size="lg"
            title="Customer Insight"
            isOpen={viewModal}
            toggle={() => setViewModal(!viewModal)}
            showClose
            scrollable={false}>
            <ShowSingleElement
              element={selectedInsight}
              icon="bx bx-message-square-detail"
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DailyInsights;
